import { DiaryItemType } from "../../models/diaryTypes";
import type { DiaryItem } from "../../models/diaryTypes";

function getAnchorIso(item: DiaryItem): string | null {
    return item.type === DiaryItemType.Task
        ? (item.dueUtc ?? null)
        : (item.startUtc ?? null);
}

// Returns YYYY-MM-DD in a given IANA timezone (e.g. "Europe/London")
export function getDateKey(isoUtc: string, timeZone: string): string {
    const dt = new Date(isoUtc);
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(dt);

    const y = parts.find(p => p.type === "year")?.value ?? "0000";
    const m = parts.find(p => p.type === "month")?.value ?? "00";
    const d = parts.find(p => p.type === "day")?.value ?? "00";
    return `${y}-${m}-${d}`;
}

export function groupByDate(items: DiaryItem[], timeZone: string) {
    // Filter items that have an anchor date/time
    const withAnchor = items
        .map(i => ({ item: i, anchor: getAnchorIso(i) }))
        .filter(x => !!x.anchor) as { item: DiaryItem; anchor: string }[];

    // Sort by anchor time
    withAnchor.sort((a, b) => new Date(a.anchor).getTime() - new Date(b.anchor).getTime());

    const map = new Map<string, DiaryItem[]>();
    for (const x of withAnchor) {
        const key = getDateKey(x.anchor, timeZone);
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(x.item);
    }

    return Array.from(map.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([dateKey, groupItems]) => ({ dateKey, items: groupItems }));
}

export function formatDateHeader(dateKey: string, timeZone: string): string {
    // dateKey is YYYY-MM-DD
    const dt = new Date(`${dateKey}T00:00:00Z`);
    // Format as e.g. "Tue 21 Jan 2026"
    return new Intl.DateTimeFormat("en-GB", {
        timeZone,
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
    }).format(dt);
}

export function relativeLabel(dateKey: string, timeZone: string): string | null {
    const now = new Date();
    const todayKey = getDateKey(now.toISOString(), timeZone);

    // Compute tomorrow/yesterday keys by stepping local day using UTC midnight proxy
    const base = new Date(`${todayKey}T00:00:00Z`);
    const tomorrowKey = getDateKey(new Date(base.getTime() + 86400000).toISOString(), timeZone);
    const yesterdayKey = getDateKey(new Date(base.getTime() - 86400000).toISOString(), timeZone);

    if (dateKey === todayKey) return "Today";
    if (dateKey === tomorrowKey) return "Tomorrow";
    if (dateKey === yesterdayKey) return "Yesterday";
    return null;
}
